/* ==========================================================================
   T.P. Mills Putter Finder - lead capture forms (Launch Candidate)

   The "Email my result" panel and the handmade inquiry panel. Split into its
   own asset for two reasons: it is the only part of the finder that touches a
   customer-entered value, so it is worth reviewing in isolation; and keeping
   every asset small keeps theme uploads verifiable.

   Nothing is written to localStorage, sessionStorage or cookies. A submitted
   value exists only inside the form element until the browser posts it.

   Contract: publishes window.PutterFinderForms.create(ctx) -> { emailResultSection,
   handmadeInquirySection }.

   ctx = { state, questions, el, track, announce, basePayload, disclosure,
           meaningfulCount, contactEmail }
   ========================================================================== */
(function (global) {
  'use strict';

  function create(ctx) {
    var state = ctx.state;
    var QUESTIONS = ctx.questions;
    var el = ctx.el;
    var track = ctx.track;
    var basePayload = ctx.basePayload;
    var disclosure = ctx.disclosure;
    var meaningfulCount = ctx.meaningfulCount;
    var CONTACT_EMAIL = ctx.contactEmail;

  /* ------------------------------------------------------ email / inquiry ---
     Both use a NATIVE Shopify contact form POST (form_type=contact to /contact)
     rather than fetch. Native posting keeps Shopify's own spam protection in
     the loop, needs no external script, and cannot silently swallow a lead the
     way a blocked AJAX call can. A mailto link sits beside it as a visible
     fallback so there is always a working path.

     Nothing is written to storage. Values exist only inside the form element
     until the browser submits it. */

  function contactForm(opts) {
    var form = el('form', 'putter-finder__form');
    form.method = 'post';
    form.action = '/contact';
    form.setAttribute('accept-charset', 'UTF-8');

    [['form_type', 'contact'], ['utf8', '\u2713']].forEach(function (pair) {
      var h = document.createElement('input');
      h.type = 'hidden';
      h.name = pair[0];
      h.value = pair[1];
      form.appendChild(h);
    });

    var emailId = opts.idPrefix + '-email';
    var nameId = opts.idPrefix + '-name';

    var el1 = el('label', 'putter-finder__label', 'Email');
    el1.setAttribute('for', emailId);
    form.appendChild(el1);
    var email = document.createElement('input');
    email.type = 'email';
    email.id = emailId;
    email.name = 'contact[email]';
    email.required = true;
    email.autocomplete = 'email';
    email.className = 'putter-finder__input';
    form.appendChild(email);

    var el2 = el('label', 'putter-finder__label', 'First name (optional)');
    el2.setAttribute('for', nameId);
    form.appendChild(el2);
    var name = document.createElement('input');
    name.type = 'text';
    name.id = nameId;
    name.name = 'contact[name]';
    name.autocomplete = 'given-name';
    name.className = 'putter-finder__input';
    form.appendChild(name);

    var body = document.createElement('input');
    body.type = 'hidden';
    body.name = 'contact[body]';
    body.value = opts.body;
    form.appendChild(body);

    var submit = el('button', 'putter-finder__btn putter-finder__btn--primary', opts.submitLabel);
    submit.type = 'submit';
    form.appendChild(submit);

    form.addEventListener('submit', function () {
      track(opts.submitEvent, Object.assign(basePayload(), opts.submitPayload || {}));
    });

    var alt = el('p', 'putter-finder__form-alt');
    alt.appendChild(document.createTextNode('Prefer your own mail app? '));
    var a = el('a', null, 'Send it directly');
    a.href = 'mailto:' + CONTACT_EMAIL +
      '?subject=' + encodeURIComponent(opts.subject) +
      '&body=' + encodeURIComponent(opts.body);
    a.addEventListener('click', function () {
      track(opts.submitEvent, Object.assign(basePayload(), opts.submitPayload || {}, { method: 'mailto' }));
    });
    alt.appendChild(a);
    alt.appendChild(document.createTextNode('.'));

    var wrap = el('div');
    wrap.appendChild(form);
    wrap.appendChild(alt);
    return wrap;
  }

  function answersSummary() {
    return QUESTIONS.map(function (q) {
      var a = state.answers[q.key];
      return q.axis + ': ' + (a ? a.label : '-');
    }).join('\n');
  }

  function resultSummaryText(ranked, labelInfo, profile, decision) {
    var lines = [];
    lines.push('T.P. Mills Putter Finder result');
    lines.push('');
    lines.push('Profile: ' + profile.name);
    lines.push('Match: ' + labelInfo.label);
    lines.push('Best match: ' + ranked.best.product.title + ' (' + ranked.best.product.handle + ')');
    lines.push('Alternatives: ' + ranked.alternatives.map(function (a) { return a.product.title; }).join(', '));
    lines.push('Selected length: ' + (decision && decision.length ? decision.length + ' inches' : 'not selected'));
    lines.push('Answers answered: ' + Object.keys(state.answers).length);
    lines.push('Answers carrying signal: ' + meaningfulCount());
    lines.push('');
    lines.push('Answers:');
    lines.push(answersSummary());
    lines.push('');
    lines.push('Page: ' + (typeof window !== 'undefined' ? window.location.href : ''));
    return lines.join('\n');
  }

  function emailResultSection(ranked, labelInfo, profile, decision) {
    var panel = disclosure('Email my result', 'Hide email form', 'putter-finder-email-body', function () {
      track('finder_email_result_opened', Object.assign(basePayload(), {
        result_profile: profile.name,
        best_match_handle: ranked.best.product.handle,
      }));
    });
    panel.classList.add('putter-finder__email');

    panel.body.appendChild(el('p', 'putter-finder__panel-copy',
      'Want to save this recommendation? Enter your email and we\u2019ll send your result summary.'));
    panel.body.appendChild(contactForm({
      idPrefix: 'pf-email-result',
      subject: 'My T.P. Mills Putter Finder result',
      body: resultSummaryText(ranked, labelInfo, profile, decision),
      submitLabel: 'Email my result',
      submitEvent: 'finder_email_result_submitted',
      submitPayload: {
        result_profile: profile.name,
        best_match_handle: ranked.best.product.handle,
        alternative_handles: ranked.alternatives.map(function (a) { return a.product.handle; }),
        raw_score_internal: ranked.best.pct,
        selected_length: decision ? decision.length : null,
      },
    }));
    return panel;
  }

  function handmadeInquirySection(product) {
    var panel = disclosure('Send handmade inquiry', 'Hide inquiry form', 'putter-finder-inquiry-body', function () {
      track('finder_handmade_inquiry_started', Object.assign(basePayload(), {
        clicked_handle: product.handle,
      }));
    });
    panel.classList.add('putter-finder__inquiry');

    panel.body.appendChild(el('p', 'putter-finder__panel-copy',
      'Ask about availability, length, or a custom build for this piece.'));
    panel.body.appendChild(contactForm({
      idPrefix: 'pf-inquiry',
      subject: 'Handmade inquiry: ' + product.title,
      body: 'Handmade / SGSS availability inquiry\n\n' +
        'Product: ' + product.title + ' (' + product.handle + ')\n' +
        'Page: ' + (typeof window !== 'undefined' ? window.location.href : '') + '\n\n' +
        'Answers:\n' + answersSummary() + '\n\nMessage:\n',
      submitLabel: 'Send inquiry',
      submitEvent: 'finder_handmade_inquiry_submitted',
      submitPayload: { clicked_handle: product.handle },
    }));
    return panel;
  }


    return {
      emailResultSection: emailResultSection,
      handmadeInquirySection: handmadeInquirySection,
    };
  }

  global.PutterFinderForms = { create: create };
})(typeof window !== 'undefined' ? window : globalThis);
